import { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Chip } from '@cbhq/cds-web/chips';
import { SearchInput } from '@cbhq/cds-web/controls';
import type { DropdownRef } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { TextCaption } from '@cbhq/cds-web/typography/TextCaption';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';
import { noop } from '@cbhq/identity-shared/utils';

import { NavigationDropdown } from '../components';
import { NavigationDropdownContent } from '../components/NavigationDropdownContent';

import { OrgSwitcherFallback } from './components/OrgSwitcherFallback';
import { ResourceHeader } from './components/ResourceHeader';
import { ResourceItem } from './components/ResourceItem';
import { ResourceMedia } from './components/ResourceMedia';
import { useAnalytics } from './hooks/useAnalytics';
import { useLogEventOnMount } from './hooks/useLogEventOnMount';
import { useResourceSearch } from './hooks/useResourceSearch';
import { HEADER_HEIGHT, MAX_HEIGHT } from './constants';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'OrgSwitcher.searchPlaceholder',
    defaultMessage: 'Search',
    description: 'Placeholder for the search input in the org switcher',
  },
});

export type OrgSwitcherProps = {
  resources: CoinbaseProfileMenuResource[] | undefined;
  activeResource: CoinbaseProfileMenuResource | undefined;
  activeResourceId: string | undefined;
  onResourceSwitch: (resourceId: string) => void;
  /** Pre-formatted count caption, e.g. "Entities (6)". Shown when search is visible. */
  searchCountLabel?: string;
  /** Whether to show the search input when there are many resources. Defaults to true. */
  searchEnabled?: boolean;
  /**
   * Enable the redesigned switcher: no header, no pill leading icon, and a
   * leading icon on each resource row. Defaults to false.
   */
  redesignEnabled?: boolean;
};

export function OrgSwitcher({
  resources,
  activeResource,
  activeResourceId,
  onResourceSwitch,
  searchCountLabel,
  searchEnabled = true,
  redesignEnabled = false,
}: OrgSwitcherProps) {
  const { formatMessage } = useIntl();
  const { logEvent } = useAnalytics();
  const dropdownRef = useRef<DropdownRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { searchQuery, handleSearchChange, searchFilteredResources, shouldShowSearch } =
    useResourceSearch({ resources, activeResourceId });

  const resourceCount = resources?.length ?? 0;

  // Skip only the empty case (component returns null). The fallback loader
  // path (multi resources, no active yet) still counts as a view.
  useLogEventOnMount(
    'org_switcher_view',
    {
      action: ActionType.render,
      componentType: ComponentType.button,
      resource_count: resourceCount,
    },
    AnalyticsEventImportance.high,
    resourceCount > 0,
  );

  const handleResourceItemPress = useCallback(
    (resourceId: string) => {
      const resource = resources?.find((r) => r.identityAccountId === resourceId);
      logEvent('org_switcher_resource_click', {
        action: ActionType.click,
        componentType: ComponentType.button,
        resource_type: resource?.resourceType,
      });
      onResourceSwitch(resourceId);
      // Close the dropdown on selection — the resource rows are custom cells, so
      // the Dropdown's built-in close-on-option-change never fires.
      dropdownRef.current?.closeMenu();
    },
    [logEvent, onResourceSwitch, resources],
  );

  const resourceList = useMemo(() => {
    return (
      <NavigationDropdownContent
        maxHeight={`${MAX_HEIGHT - HEADER_HEIGHT}px`}
        hideScrollbar={redesignEnabled}
      >
        {searchFilteredResources?.map((resource) => {
          return (
            <ResourceItem
              key={resource.identityAccountId}
              resource={resource}
              onPress={handleResourceItemPress}
              selected={resource.identityAccountId === activeResourceId}
              showLeadingIcon={redesignEnabled}
            />
          );
        })}
      </NavigationDropdownContent>
    );
  }, [searchFilteredResources, handleResourceItemPress, activeResourceId, redesignEnabled]);

  const searchInput = useMemo(() => {
    if (!searchEnabled || !shouldShowSearch) {
      return null;
    }
    return (
      <VStack>
        <TextCaption as="span" color="fgMuted" paddingBottom={2} paddingX={3}>
          {searchCountLabel}
        </TextCaption>
        <Box paddingX={2} paddingBottom={2}>
          <SearchInput
            placeholder={formatMessage(messages.searchPlaceholder)}
            value={searchQuery}
            onChangeText={handleSearchChange}
            testID="org-switcher-search"
            compact
          />
        </Box>
      </VStack>
    );
  }, [
    searchEnabled,
    shouldShowSearch,
    searchCountLabel,
    formatMessage,
    searchQuery,
    handleSearchChange,
  ]);

  const content = useMemo(() => {
    return (
      <VStack overflow="hidden">
        {!redesignEnabled && <ResourceHeader selectedResource={activeResource} />}
        {searchInput}
        {resourceList}
      </VStack>
    );
  }, [redesignEnabled, activeResource, searchInput, resourceList]);

  const handleOpenMenu = useCallback(() => setIsOpen(true), []);
  const handleCloseMenu = useCallback(() => setIsOpen(false), []);

  // Memoized so the chevron's style object isn't recreated every render.
  const chevronStyle = useMemo(
    () => ({
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 150ms ease',
    }),
    [isOpen],
  );

  if (!resources || resources.length === 0) {
    return null;
  }

  // Single resource → show read-only chip
  // Callers are responsible for filtering out single user-type resources before rendering this component
  if (resources.length === 1) {
    const singleResource = resources[0];
    return (
      <Tooltip content={singleResource.name} placement="bottom">
        <Chip
          start={redesignEnabled ? undefined : <ResourceMedia resource={singleResource} small />}
          maxWidth={200}
          height={40}
          onClick={noop}
        >
          {singleResource.name}
        </Chip>
      </Tooltip>
    );
  }

  if (!activeResource) {
    /**
     * Having more than 1 resource but no active resource means we're still defining
     * the active resource.
     */
    return <OrgSwitcherFallback />;
  }

  return (
    <NavigationDropdown
      ref={dropdownRef}
      content={content}
      value={activeResourceId}
      testID="AppSwitcherContent-Dropdown"
      onChange={onResourceSwitch}
      onOpenMenu={handleOpenMenu}
      onCloseMenu={handleCloseMenu}
    >
      <Tooltip content={activeResource.name} placement="bottom">
        <Chip
          start={redesignEnabled ? undefined : <ResourceMedia resource={activeResource} small />}
          end={
            // Rotate the chevron when the dropdown opens/closes.
            <Box display="flex" style={chevronStyle}>
              <Icon name="caretDown" size="s" color="fgMuted" />
            </Box>
          }
          maxWidth={200}
          height={40}
          onClick={noop}
        >
          {activeResource.name}
        </Chip>
      </Tooltip>
    </NavigationDropdown>
  );
}
